import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Calculator,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  AlertTriangle,
  DollarSign,
  Percent,
  Info,
  TrendingUp,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';

interface TenantFeeConfig {
  id: string;
  tenantId: string;
  operationType: string;
  fixedFee: number;
  percentageFee: number;
  minimumFee: number;
  maximumFee?: number;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  platformMinimum?: {
    fixedFee: number;
    percentageFee: number;
    minimumFee: number;
  };
  margin?: {
    fixedMargin: number;
    percentageMargin: number;
  };
}

interface PlatformMinimum {
  fixedFee: number;
  percentageFee: number;
  minimumFee: number;
  maximumFee?: number;
}

// Platform minimums come as a Record<operationType, PlatformMinimum>
type PlatformMinimumsMap = Record<string, PlatformMinimum>;

interface FormData {
  operationType: string;
  fixedFee: string;
  percentageFee: string;
  minimumFee: string;
  maximumFee: string;
  effectiveFrom: string;
  effectiveTo: string;
  isActive: boolean;
  justification: string;
}

const OPERATION_TYPES = [
  { value: 'PIX_RECEIVE', label: 'Pix Recebido' },
  { value: 'PIX_SEND', label: 'Pix Enviado' },
  { value: 'TRANSFER', label: 'Transferência' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'CARD', label: 'Cartão' },
  { value: 'WITHDRAWAL', label: 'Saque' },
  { value: 'MAINTENANCE', label: 'Manutenção' },
];

const OPERATION_LABELS: Record<string, string> = {
  PIX_RECEIVE: 'Pix Recebido',
  PIX_SEND: 'Pix Enviado',
  TRANSFER: 'Transferência',
  BOLETO: 'Boleto',
  CARD: 'Cartão',
  WITHDRAWAL: 'Saque',
  MAINTENANCE: 'Manutenção',
};

const initialFormData: FormData = {
  operationType: 'PIX_RECEIVE',
  fixedFee: '0',
  percentageFee: '0',
  minimumFee: '0',
  maximumFee: '',
  effectiveFrom: new Date().toISOString().split('T')[0],
  effectiveTo: '',
  isActive: true,
  justification: '',
};

export function TenantFeeConfig() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSimulatorOpen, setIsSimulatorOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<TenantFeeConfig | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [simulatorAmount, setSimulatorAmount] = useState('10000');
  const [simulatorOperation, setSimulatorOperation] = useState('PIX_RECEIVE');

  // Fetch tenant fee configs
  const { data: configs, isLoading, refetch } = useQuery<TenantFeeConfig[]>({
    queryKey: ['tenant-fee-configs'],
    queryFn: async () => {
      const res = await api.get(ENDPOINTS.feeCascade.tenantFees.list);
      return res.data;
    },
  });

  // Fetch platform minimums (returns Record<operationType, PlatformMinimum>)
  const { data: platformMinimums } = useQuery<PlatformMinimumsMap>({
    queryKey: ['platform-minimums'],
    queryFn: async () => {
      const res = await api.get(ENDPOINTS.feeCascade.tenantFees.platformMinimums);
      return res.data;
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: Partial<FormData>) => {
      const payload = {
        operationType: data.operationType,
        fixedFee: Math.round(parseFloat(data.fixedFee || '0') * 100),
        percentageFee: parseFloat(data.percentageFee || '0'),
        minimumFee: Math.round(parseFloat(data.minimumFee || '0') * 100),
        maximumFee: data.maximumFee ? Math.round(parseFloat(data.maximumFee) * 100) : undefined,
        effectiveFrom: data.effectiveFrom || undefined,
        effectiveTo: data.effectiveTo || undefined,
        isActive: data.isActive,
      };
      const res = await api.post(ENDPOINTS.feeCascade.tenantFees.create, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-fee-configs'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<FormData> }) => {
      const payload = {
        fixedFee: Math.round(parseFloat(data.fixedFee || '0') * 100),
        percentageFee: parseFloat(data.percentageFee || '0'),
        minimumFee: Math.round(parseFloat(data.minimumFee || '0') * 100),
        maximumFee: data.maximumFee ? Math.round(parseFloat(data.maximumFee) * 100) : undefined,
        effectiveFrom: data.effectiveFrom || undefined,
        effectiveTo: data.effectiveTo || undefined,
        isActive: data.isActive,
        justification: data.justification,
      };
      const res = await api.put(ENDPOINTS.feeCascade.tenantFees.update(id), payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-fee-configs'] });
      setIsModalOpen(false);
      setEditingConfig(null);
      resetForm();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(ENDPOINTS.feeCascade.tenantFees.delete(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-fee-configs'] });
    },
  });

  // Simulate margin mutation
  const simulateMutation = useMutation({
    mutationFn: async ({ operationType, amount }: { operationType: string; amount: number }) => {
      const res = await api.post(ENDPOINTS.feeCascade.tenantFees.simulateMargin, {
        operationType,
        amount,
      });
      return res.data;
    },
  });

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingConfig(null);
  };

  const handleEdit = (config: TenantFeeConfig) => {
    setEditingConfig(config);
    setFormData({
      operationType: config.operationType,
      fixedFee: (config.fixedFee / 100).toString(),
      percentageFee: config.percentageFee.toString(),
      minimumFee: (config.minimumFee / 100).toString(),
      maximumFee: config.maximumFee ? (config.maximumFee / 100).toString() : '',
      effectiveFrom: config.effectiveFrom?.split('T')[0] || '',
      effectiveTo: config.effectiveTo?.split('T')[0] || '',
      isActive: config.isActive,
      justification: '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingConfig) {
      updateMutation.mutate({ id: editingConfig.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleSimulate = () => {
    const amount = Math.round(parseFloat(simulatorAmount) * 100);
    simulateMutation.mutate({ operationType: simulatorOperation, amount });
  };

  // Get platform minimum for selected operation (now using object lookup instead of array.find)
  const getMinimumForOperation = (operationType: string): PlatformMinimum | undefined => {
    return platformMinimums?.[operationType];
  };

  const selectedMinimum = getMinimumForOperation(formData.operationType);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Minhas Taxas</h1>
          <p className="text-surface-400 mt-1">
            Configure as taxas cobradas dos seus clientes por tipo de operação
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => setIsSimulatorOpen(true)}
          >
            <Calculator className="w-4 h-4 mr-2" />
            Simulador de Margem
          </Button>
          <Button
            variant="ghost"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Taxa
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-primary-500/10 border-primary-500/30">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-primary-400 mt-0.5" />
          <div>
            <h3 className="font-medium text-primary-300">Como funciona a cascata de taxas</h3>
            <p className="text-sm text-surface-400 mt-1">
              Suas taxas devem ser maiores ou iguais às taxas mínimas da plataforma.
              A diferença entre sua taxa e a taxa da plataforma é sua margem de lucro.
            </p>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-primary-500 animate-spin" />
          </div>
        ) : !configs || configs.length === 0 ? (
          <div className="text-center py-12">
            <Calculator className="w-12 h-12 text-surface-600 mx-auto mb-4" />
            <p className="text-surface-400">Nenhuma configuração de taxa encontrada</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Operação</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Taxas</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Limites</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Margem</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-surface-400">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-700">
              {configs.map((config) => (
                <tr key={config.id} className="hover:bg-surface-800/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-primary-400" />
                      <span className="font-medium text-white">
                        {OPERATION_LABELS[config.operationType] || config.operationType}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <DollarSign className="w-3 h-3 text-surface-400" />
                        <span className="text-surface-300">Fixa: {formatCurrency(config.fixedFee)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Percent className="w-3 h-3 text-surface-400" />
                        <span className="text-surface-300">Percentual: {config.percentageFee}%</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1 text-sm">
                      <div className="text-surface-300">Mín: {formatCurrency(config.minimumFee)}</div>
                      {config.maximumFee && (
                        <div className="text-surface-300">Máx: {formatCurrency(config.maximumFee)}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1 text-sm">
                      {config.margin ? (
                        <>
                          <div className="text-green-400">+{formatCurrency(config.margin.fixedMargin)} fixo</div>
                          <div className="text-green-400">+{config.margin.percentageMargin}% var</div>
                        </>
                      ) : (
                        <span className="text-surface-500">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={config.isActive ? 'success' : 'warning'}>
                      {config.isActive ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(config)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm('Tem certeza que deseja excluir esta configuração?')) {
                            deleteMutation.mutate(config.id);
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>


      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingConfig ? 'Editar Taxa' : 'Nova Taxa'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {!editingConfig && (
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Tipo de Operação
              </label>
              <Select
                value={formData.operationType}
                onChange={(e) => setFormData({ ...formData, operationType: e.target.value })}
              >
                {OPERATION_TYPES.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {/* Platform Minimum Info */}
          {selectedMinimum && (
            <div className="p-3 bg-surface-800/50 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-surface-400 mb-2">
                <AlertTriangle className="w-4 h-4 text-yellow-400" />
                Mínimo da Plataforma
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <span className="text-surface-500">Fixa:</span>
                  <span className="text-white ml-1">{formatCurrency(selectedMinimum.fixedFee)}</span>
                </div>
                <div>
                  <span className="text-surface-500">%:</span>
                  <span className="text-white ml-1">{selectedMinimum.percentageFee}%</span>
                </div>
                <div>
                  <span className="text-surface-500">Mín:</span>
                  <span className="text-white ml-1">{formatCurrency(selectedMinimum.minimumFee)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Taxa Fixa (R$)"
              type="number"
              step="0.01"
              min={selectedMinimum ? (selectedMinimum.fixedFee / 100).toString() : '0'}
              value={formData.fixedFee}
              onChange={(e) => setFormData({ ...formData, fixedFee: e.target.value })}
              placeholder="0.00"
            />
            <Input
              label="Taxa Percentual (%)"
              type="number"
              step="0.01"
              min={selectedMinimum ? selectedMinimum.percentageFee.toString() : '0'}
              max="100"
              value={formData.percentageFee}
              onChange={(e) => setFormData({ ...formData, percentageFee: e.target.value })}
              placeholder="0.00"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Taxa Mínima (R$)"
              type="number"
              step="0.01"
              min={selectedMinimum ? (selectedMinimum.minimumFee / 100).toString() : '0'}
              value={formData.minimumFee}
              onChange={(e) => setFormData({ ...formData, minimumFee: e.target.value })}
              placeholder="0.00"
            />
            <Input
              label="Taxa Máxima (R$) - Opcional"
              type="number"
              step="0.01"
              min="0"
              value={formData.maximumFee}
              onChange={(e) => setFormData({ ...formData, maximumFee: e.target.value })}
              placeholder="Sem limite"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Vigência Início"
              type="date"
              value={formData.effectiveFrom}
              onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value })}
            />
            <Input
              label="Vigência Fim (Opcional)"
              type="date"
              value={formData.effectiveTo}
              onChange={(e) => setFormData({ ...formData, effectiveTo: e.target.value })}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="rounded border-surface-600 bg-surface-800 text-primary-500 focus:ring-primary-500"
            />
            <label htmlFor="isActive" className="text-sm text-surface-300">
              Configuração ativa
            </label>
          </div>

          {editingConfig && (
            <Input
              label="Justificativa da Alteração"
              value={formData.justification}
              onChange={(e) => setFormData({ ...formData, justification: e.target.value })}
              placeholder="Motivo da alteração..."
            />
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {editingConfig ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Margin Simulator Modal */}
      <Modal
        isOpen={isSimulatorOpen}
        onClose={() => setIsSimulatorOpen(false)}
        title="Simulador de Margem"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">
              Tipo de Operação
            </label>
            <Select
              value={simulatorOperation}
              onChange={(e) => setSimulatorOperation(e.target.value)}
            >
              {OPERATION_TYPES.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </Select>
          </div>

          <Input
            label="Valor da Operação (R$)"
            type="number"
            step="0.01"
            min="0"
            value={simulatorAmount}
            onChange={(e) => setSimulatorAmount(e.target.value)}
            placeholder="100.00"
          />

          <Button
            onClick={handleSimulate}
            disabled={simulateMutation.isPending}
            className="w-full"
          >
            {simulateMutation.isPending ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Calculator className="w-4 h-4 mr-2" />
            )}
            Simular
          </Button>

          {simulateMutation.data && (
            <div className="mt-4 p-4 bg-surface-800/50 rounded-lg space-y-3">
              <h4 className="font-medium text-white">Resultado da Simulação</h4>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-surface-400">Valor da Operação:</span>
                  <span className="text-white">{formatCurrency(simulateMutation.data.amount)}</span>
                </div>
                <div className="border-t border-surface-700 pt-2">
                  <p className="text-surface-400 text-xs mb-2">Taxa da Plataforma</p>
                  <div className="flex justify-between">
                    <span className="text-surface-400">Taxa Plataforma:</span>
                    <span className="text-orange-400">{formatCurrency(simulateMutation.data.platformFee)}</span>
                  </div>
                </div>
                <div className="border-t border-surface-700 pt-2">
                  <p className="text-surface-400 text-xs mb-2">Sua Taxa</p>
                  <div className="flex justify-between">
                    <span className="text-surface-400">Taxa Cobrada:</span>
                    <span className="text-white">{formatCurrency(simulateMutation.data.tenantFee)}</span>
                  </div>
                </div>
                <div className="border-t border-surface-700 pt-2 flex justify-between">
                  <span className="text-surface-300 font-medium">Sua Margem:</span>
                  <span className="text-green-400 font-bold">
                    {formatCurrency(simulateMutation.data.tenantMargin)}
                  </span>
                </div>
              </div>

              {simulateMutation.data.tenantMargin > 0 && (
                <div className="flex items-center gap-2 text-xs text-green-400">
                  <TrendingUp className="w-3 h-3" />
                  Margem positiva de {((simulateMutation.data.tenantMargin / simulateMutation.data.amount) * 100).toFixed(2)}%
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default TenantFeeConfig;
